/**
 * km-griff-addon.js
 *
 * Fügt bei Fusion5-Rasierklingen-Produkten einen optionalen "Griff"-Zusatzkauf hinzu.
 * Schritt 191: Griff-Farben werden direkt als Bildkacheln angezeigt (keine Checkbox mehr).
 * Der Griff wird als eigenständige, separate Warenkorbposition (echte Produktvariante)
 * hinzugefügt, sobald das Haupt-Produktformular abgeschickt wird – unabhängig und ohne
 * die native Add-to-Cart-Logik des Themes zu verändern oder zu blockieren.
 *
 * Nutzt bewusst dieselben offiziellen Bausteine wie das Theme selbst:
 * - Shopify AJAX Cart API (/cart/add.js) für den eigentlichen Warenkorb-Zugriff
 * - Das globale "@shopify/events" Standard-Events-Modul, damit Warenkorb-Drawer und
 *   Cart-Bubble automatisch und korrekt aktualisiert werden (genau wie beim normalen
 *   "In den Warenkorb"-Klick).
 */
(async () => {
  let CartLinesUpdateEvent;
  let CartErrorEvent;

  try {
    const events = await import('@shopify/events');
    CartLinesUpdateEvent = events.CartLinesUpdateEvent;
    CartErrorEvent = events.CartErrorEvent;
  } catch (err) {
    console.warn('[km-griff-addon] Konnte @shopify/events nicht laden, Cart-UI-Updates evtl. verzögert.', err);
  }

  const getCartAddUrl = () => {
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) {
      return Theme.routes.cart_add_url;
    }
    return '/cart/add.js';
  };

  const getCartUrl = () => {
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) {
      return `${Theme.routes.cart_url}.js`;
    }
    return '/cart.js';
  };

  const getSectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return Array.from(ids);
  };

  /**
   * Shopify legt die Warenkorb-Session (Cookie "cart") erst beim ERSTEN Zugriff auf
   * die Cart-API an. Wenn zwei /cart/add.js-Requests (der native Theme-Submit UND
   * unser eigener Griff-Add) gleichzeitig auf einen noch leeren/sessionlosen Warenkorb
   * treffen, kann es serverseitig zu einer Race Condition kommen: beide Requests legen
   * unabhängig voneinander eine neue Cart-Session an, und nur die zuletzt im Browser
   * verarbeitete "Set-Cookie"-Antwort gewinnt – der jeweils andere Request landet dann
   * in einem verwaisten, nie sichtbaren Warenkorb.
   *
   * Fix: Wir "wärmen" die Cart-Session proaktiv per einfachem GET auf /cart.js vor –
   * einmal direkt beim Laden der Seite (in aller Regel liegen zwischen Seitenaufbau und
   * Klick auf "In den Warenkorb" mehrere Sekunden, sodass die Session längst steht) und
   * zusätzlich sobald eine Griff-Kachel ausgewählt wird. onSubmit() wartet zusätzlich
   * auf das Ergebnis dieses Warm-up, bevor der eigentliche Griff-Add gestartet wird.
   * Das ist rein lesend (GET), verändert nichts am Warenkorb und hat keinerlei
   * Nebenwirkungen auf die native Add-to-Cart-Logik.
   */
  let cartSessionWarmup = null;
  function ensureCartSession() {
    if (!cartSessionWarmup) {
      cartSessionWarmup = fetch(getCartUrl(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).catch(() => {});
    }
    return cartSessionWarmup;
  }
  ensureCartSession();

  /**
   * Schritt 191 – Fix Race Condition (Artikel ging verloren):
   * Der native Add-to-Cart-Request des Themes und der Griff-Request liefen bisher
   * PARALLEL. Test ergab: Bei neuer Sitzung (noch kein Warenkorb) ODER bei gleichzeitigen
   * Schreibzugriffen auf denselben Warenkorb ging dabei einer der beiden Artikel verloren.
   * Lösung: Der Griff wird erst hinzugefügt, NACHDEM der native Request abgeschlossen ist.
   * Das Theme meldet seinen Add über das Standard-Event "shopify:cart:lines-update"
   * (dispatcht von <product-form-component>, mit Promise). Wir warten auf dieses Promise.
   * Eigene Events (dispatcht auf document) werden ignoriert. Timeout: 8 s.
   */
  const LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
  // Hinweis: Das Theme löst sein Event bereits VOR dem "submit"-Event des Formulars aus.
  // Deshalb lauschen wir dauerhaft und merken uns das zuletzt ausgelöste native Event.
  let lastNativeAdd = null;
  document.addEventListener(
    LINES_UPDATE_EVENT,
    (event) => {
      if (event.target === document) return; // eigenes Griff-Event ignorieren
      lastNativeAdd = { at: performance.now(), promise: event.promise, used: false };
    },
    true
  );

  function waitForNativeCartAdd(timeoutMs = 8000) {
    const startedAt = performance.now();
    return new Promise((resolve) => {
      let finished = false;
      let poll = null;
      let timer = null;
      const finish = (result) => {
        if (finished) return;
        finished = true;
        clearInterval(poll);
        clearTimeout(timer);
        resolve(result);
      };
      const tryUse = () => {
        if (lastNativeAdd && !lastNativeAdd.used && lastNativeAdd.at >= startedAt - 1500) {
          lastNativeAdd.used = true;
          clearInterval(poll);
          const p = lastNativeAdd.promise;
          if (p && typeof p.then === 'function') {
            p.then(() => finish('ok'), () => finish('failed'));
          } else {
            setTimeout(() => finish('ok'), 1000);
          }
          return true;
        }
        return false;
      };
      timer = setTimeout(() => finish('timeout'), timeoutMs);
      if (!tryUse()) poll = setInterval(tryUse, 50);
    });
  }

  /**
   * Fügt eine Griff-Variante als eigene Position zum Warenkorb hinzu und
   * synchronisiert Warenkorb-Drawer / Cart-Bubble über das Standard-Event-System
   * des Themes (dieselbe Mechanik wie beim regulären "In den Warenkorb").
   */
  async function addGriffToCart(variantId) {
    const sectionIds = getSectionIds();

    let deferred = null;
    if (CartLinesUpdateEvent) {
      deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferred.promise,
        })
      );
    }

    const response = await fetch(getCartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
      }),
    }).then((res) => res.json());

    if (response && response.status) {
      const message = response.message || 'Der Griff konnte nicht zum Warenkorb hinzugefügt werden.';
      if (deferred) deferred.reject(new Error(message));
      if (CartErrorEvent) {
        document.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
      }
      throw new Error(message);
    }

    // Aktuellen Warenkorb holen, damit cart-icon.js etc. die korrekte Gesamtmenge erhalten.
    let cartAjax = null;
    try {
      cartAjax = await fetch(getCartUrl(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());
    } catch (err) {
      // Nicht kritisch – der Artikel wurde bereits erfolgreich hinzugefügt.
      console.warn('[km-griff-addon] Warenkorb-Refresh fehlgeschlagen:', err);
    }

    if (deferred) {
      deferred.resolve({
        cart: cartAjax && CartLinesUpdateEvent ? CartLinesUpdateEvent.createCartFromAjaxResponse(cartAjax) : undefined,
        detail: {
          items: cartAjax ? cartAjax.items : undefined,
          source: 'km-griff-addon',
          sections: response.sections,
          didError: false,
        },
      });
    }

    return response;
  }

  class KmGriffAddon {
    constructor(root) {
      this.root = root;
      this.productId = root.dataset.productId;
      this.variantInputs = Array.from(root.querySelectorAll('[data-km-griff-variant]'));
      this.status = root.querySelector('[data-km-griff-status]');
      this.busy = false;

      this.form = this.resolveForm();

      // Schritt 191: Griffe sind immer sichtbar. Ein Klick auf eine Griff-Kachel
      // wählt diesen Griff aus (max. einer), erneuter Klick hebt die Auswahl auf.
      this.variantInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) {
            this.variantInputs.forEach((other) => {
              if (other !== input) other.checked = false;
            });
            // Session frühzeitig wärmen (siehe ensureCartSession).
            ensureCartSession();
          }
          this.setStatus('');
        });
      });

      if (this.form) {
        // Bubble-Phase-Listener direkt auf dem <form>-Element: läuft in der
        // Target-Phase, also VOR dem Bubble-Listener der übergeordneten
        // <product-form-component> (Theme-Logik). Blockiert oder verändert die
        // native Übermittlung nicht (kein preventDefault/stopPropagation).
        this.form.addEventListener('submit', () => this.onSubmit());
      }
    }

    resolveForm() {
      if (this.productId) {
        const scoped = document.querySelector(
          `product-form-component[data-product-id="${this.productId}"] form[data-type="add-to-cart-form"]`
        );
        if (scoped) return scoped;
      }
      return document.querySelector('form[data-type="add-to-cart-form"]');
    }

    get selectedVariantId() {
      const checked = this.variantInputs.find((input) => input.checked);
      return checked ? checked.value : null;
    }

    setStatus(text, state) {
      if (!this.status) return;
      this.status.textContent = text;
      if (state) {
        this.status.setAttribute('data-state', state);
      } else {
        this.status.removeAttribute('data-state');
      }
    }

    async onSubmit() {
      const variantId = this.selectedVariantId;
      if (!variantId || this.busy) return;

      // Auf den nativen Add des Themes warten (Event wurde ggf. schon kurz vorher ausgelöst).
      const nativeAdd = waitForNativeCartAdd();

      this.busy = true;
      this.root.classList.add('km-griff-addon--busy');
      this.setStatus('Griff wird hinzugefügt …');

      try {
        // Erst den nativen Add des Themes abwarten, dann den Griff hinzufügen
        // (sequenziell statt parallel – verhindert verlorene Artikel).
        await ensureCartSession();
        const nativeResult = await nativeAdd;
        if (nativeResult === 'failed') {
          this.setStatus('Der Griff wurde nicht hinzugefügt, da der Hauptartikel nicht in den Warenkorb gelegt werden konnte.', 'error');
          return;
        }
        await addGriffToCart(variantId);
        this.setStatus('Griff wurde zum Warenkorb hinzugefügt.', 'success');
      } catch (err) {
        console.error('[km-griff-addon] Fehler beim Hinzufügen des Griffs:', err);
        this.setStatus('Griff konnte nicht hinzugefügt werden. Bitte im Warenkorb prüfen.', 'error');
      } finally {
        this.busy = false;
        this.root.classList.remove('km-griff-addon--busy');
      }
    }
  }

  const init = () => {
    document.querySelectorAll('[data-km-griff-addon]').forEach((el) => {
      if (!el.__kmGriffAddonInitialized) {
        el.__kmGriffAddonInitialized = true;
        new KmGriffAddon(el);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
